import { Api } from '@/Api';
import { defineStore, storeToRefs } from 'pinia';
import { useViewerItemStore } from '@/modules/viewer/store/useViewerItemStore';
import { DEBUG_NAMESPACE, useDebug } from '@/composables/useDebug';

export interface EmbedState {
  embedServiceURL: string;
  showTitle: boolean;
  showBranding: boolean;
  iframe: HTMLIFrameElement | null;
}

const { debug } = useDebug(DEBUG_NAMESPACE.STORE_EMBED);

export const useEmbedStore = defineStore('embedStore', {
  state: () =>
    ({
      embedServiceURL: '',
      showTitle: true,
      showBranding: true,
      iframe: null,
    }) as EmbedState,
  actions: {
    initialize(url: string) {
      this.embedServiceURL = url;
    },
    toggleShowTitle() {
      this.showTitle = !this.showTitle;
      this.updateIFrameQueryParams();
    },
    toggleShowBranding() {
      this.showBranding = !this.showBranding;
      this.updateIFrameQueryParams();
    },
    // https://github.com/cloudapp/dropper_oembed/blob/90beedae60c262107504af1c6b550dbc78a5f449/models/link.ts#L28
    // not supporting (viewer) query params
    // - branding
    // - title
    async initIFrame() {
      const viewerItemStore = useViewerItemStore();
      const { shareLink } = storeToRefs(viewerItemStore);
      if (!shareLink.value) {
        debug('[init::embed:iframe] Cannot init Embed - missing shareLink');
        return;
      }

      const embed = await Api.embed(this.embedServiceURL, shareLink.value);

      const parser = new DOMParser();
      const document = parser.parseFromString(embed.html, 'text/html');
      const iframe: HTMLIFrameElement = document.getElementsByTagName('iframe')[0];

      this.iframe = iframe;
      this.updateIFrameQueryParams();
    },
    // https://github.com/cloudapp/dropper_oembed/
    // update params for embedded iframe src
    async updateIFrameQueryParams() {
      if (!this.iframe) {
        debug('[update::embed:iframe] Cannot update Embed Query Params - missing iframe');
        return;
      }

      // CLONE NODE TO CHANGE REFERENCE (store update)
      const iframe = this.iframe.cloneNode(true) as HTMLIFrameElement;
      const url = new URL(iframe.src);
      const updatedSearchParams = new URLSearchParams(url.searchParams);

      if (this.showTitle) {
        !updatedSearchParams.get('title') && updatedSearchParams.append('title', 'true');
      } else {
        updatedSearchParams.delete('title');
      }

      if (this.showBranding) {
        !updatedSearchParams.get('branding') && updatedSearchParams.append('branding', 'true');
      } else {
        updatedSearchParams.delete('branding');
      }

      if (url.searchParams.toString() !== updatedSearchParams.toString()) {
        iframe.src = `${url.origin}${url.pathname}?${updatedSearchParams.toString()}`;
        this.iframe = iframe;
      }
    },
  },
  getters: {
    embedHtml: state => state.iframe?.outerHTML,
  },
});
